import { inject, Injectable } from '@angular/core';
import { ExternalLink } from '@app/core/model/entities/external-link/external-link';
import {
  LinkConfirmationModalComponent
} from '@app/shared/popup/link-confirmation-modal/link-confirmation-modal.component';
import { TranslateService } from '@ngx-translate/core';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { PopupManager, PopupSize } from '@services/managers/popup.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { EMPTY, ignoreElements, Observable, Subject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ExternalLinkService {
  private readonly appManager = inject(AppManager);
  private readonly apiService = inject(ApiService);
  private readonly popupManager = inject(PopupManager);
  private readonly translate = inject(TranslateService);

  private externalLinkAddedSubject = new Subject<ExternalLink>();
  private externalLinkUpdatedSubject = new Subject<ExternalLink>();
  private externalLinkDeletedSubject = new Subject<number>();

  /**
   * Emits any External Link after it was added.
   */
  public get externalLinkAdded$(): Observable<ExternalLink> {
    return this.externalLinkAddedSubject.asObservable();
  }

  /**
   * Emits any External Link after it was updated.
   */
  public get externalLinkUpdated$(): Observable<ExternalLink> {
    return this.externalLinkUpdatedSubject.asObservable();
  }

  /**
   * Emits the id of the External Link that has been deleted.
   */
  public get externalLinkDeleted$(): Observable<number> {
    return this.externalLinkDeletedSubject.asObservable();
  }

  /** Get the external links of the current organization. */
  public getExternalLinksOfCurrentOrganization(): Observable<ExternalLink[]> {
    const organizationId = this.appManager.currentOrganization.id;
    const query = gql`
      query ExternalLinks($organizationId: String!) {
        externalLinks(organizationId: $organizationId) {
          id
          organizationId
          name
          url
        }
      }
    `;
    const variables = {organizationId};
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(ExternalLink, data['externalLinks'] as ExternalLink[])));
  }

  /**
   * Create an external link.
   *
   * @param name - The name of the external link
   * @param url - The url of the external link
   */
  public createExternalLink(name: string, url: string): Observable<ExternalLink> {
    const organizationId = this.appManager.currentOrganization.id;
    const mutation = gql`
      mutation CreateExternalLink($organizationId: String!, $name: String!, $url: String!) {
        createExternalLink(organizationId: $organizationId, name: $name, url: $url) {
          id
          organizationId
          name
          url
        }
      }
    `;
    const variables = {organizationId, name, url};
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(ExternalLink, data['createExternalLink'])),
        tap(newExternalLink => this.externalLinkAddedSubject.next(newExternalLink))
      );
  }

  /**
   * Update an external link.
   *
   * @param externalLinkId - The id of the external link to update
   * @param name - The new name of the external link
   * @param url - The new url of the external link
   */
  public updateExternalLink(externalLinkId: number, name: string, url: string): Observable<ExternalLink> {
    const mutation = gql`
      mutation UpdateExternalLink($externalLinkId: Int!, $name: String!, $url: String!) {
        updateExternalLink(externalLinkId: $externalLinkId, name: $name, url: $url) {
          id
          organizationId
          name
          url
        }
      }
    `;
    const variables = {externalLinkId, name, url};
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(ExternalLink, data['updateExternalLink'])),
        tap(updatedExternalLink => this.externalLinkUpdatedSubject.next(updatedExternalLink))
      );
  }

  /**
   * Delete an external link.
   *
   * @param externalLinkId - The id of the external link to delete
   */
  public deleteExternalLink(externalLinkId: number): Observable<boolean> {
    const mutation = gql`
      mutation DeleteExternalLink($externalLinkId: Int!) {
        deleteExternalLink(externalLinkId: $externalLinkId)
      }
    `;
    const variables = {externalLinkId};
    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteExternalLink'] as boolean),
        tap(success => success && this.externalLinkDeletedSubject.next(externalLinkId))
      );
  }

  /**
   * Show a confirmation popup before opening an external link.
   * Open the link in a new tab if the user confirms it, otherwise do nothing.
   * User can authorize the link to be opened without confirmation in the future.
   * If the link has been permanently allowed, open it directly.
   * @param url Link to open.
   * @returns Observable that completes when the link is opened or the action is cancelled.
   */
  public confirmExternalLink(url: string): Observable<void> {
    const localStorageKey = 'allowExternalLink';
    // If the link has been permanently allowed, open it directly
    if (localStorage.getItem(localStorageKey)) {
      window.open(url, '_blank');
      return EMPTY;
    }

    return this.popupManager.showGenericPopup(
      LinkConfirmationModalComponent,
      PopupSize.MEDIUM_SMALL,
      {
        dialogTitle: this.translate.instant('TITLE.EXTERNAL_LINK_WARNING'),
        dialogMessage: this.translate.instant('MESSAGE.EXTERNAL_LINK_WARNING'),
        type: 'warning',
      }
    ).afterClosed()
      .pipe(
        filter(response => response !== ''),
        tap(response => {
          // Allow the link to be opened without confirmation in the future
          if (response) localStorage.setItem(localStorageKey, 'true');
          window.open(url, '_blank');
        }),
        ignoreElements()
      );
  }
}
